import { Dialog, DialogActions, DialogContent, Paper } from "@mui/material";
import "./CustomDialog.scss";
import type { ReactElement, ReactNode } from "react";
import IconCrossCircle from "@/shared/components/generic/icons/IconCrossCircle.tsx";
import { colors } from "@/shared/style/design_system/Colors.ts";
import type { IconComponent } from "@/shared/components/generic/IconComponentType.ts";
import DraggablePaper from "@/shared/components/generic/custom-dialog/DraggablePaper.tsx";

interface CustomDialogProps {
  open: boolean;
  onClose: () => void;
  icon?: IconComponent;
  title?: string;
  children: ReactNode;
  buttonsNode?: ReactNode;
  draggable?: boolean;
  dontCloseOnBackdropClick?: boolean;
  paperMaxWidth?: string;
  paperWidth?: string;
  hideBackdrop?: boolean;
}

export default function CustomDialog({
  open,
  onClose,
  icon,
  title,
  children,
  buttonsNode,
  draggable = true,
  dontCloseOnBackdropClick,
  paperMaxWidth = "600px",
  paperWidth,
  hideBackdrop = false,
}: CustomDialogProps): ReactElement {
  const iconComponent = icon?.({ className: "custom-dialog-icon" });

  function handleClose(reason: string): void {
    if (reason === "backdropClick" && (dontCloseOnBackdropClick ?? false)) {
      return;
    }
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        handleClose(reason);
      }}
      classes={{ paper: "custom-dialog" }}
      className="dialog"
      PaperComponent={draggable ? DraggablePaper : Paper}
      slotProps={{
        paper: {
          sx: {
            maxWidth: paperMaxWidth,
            ...(paperWidth !== undefined ? { width: paperWidth } : {}),
          },
        },
      }}
      hideBackdrop={hideBackdrop}
    >
      <div className={`custom-dialog-header ${draggable ? "draggable" : ""}`}>
        {icon != undefined && iconComponent}
        {title != undefined && (
          <div className="custom-dialog-title">{title}</div>
        )}
        <IconCrossCircle
          color={colors.lagoon400}
          onClick={onClose}
          className="custom-dialog-close-icon"
        />
      </div>
      <DialogContent className="custom-dialog-content">
        {children}
      </DialogContent>
      {buttonsNode != undefined && (
        <DialogActions className="custom-dialog-actions">
          {buttonsNode}
        </DialogActions>
      )}
    </Dialog>
  );
}
